import axios from 'axios'
import { get, has, startsWith } from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import dayjs from '@/dayjs'
import { mock } from '@/services/mock'

const axiosInstance = axios.create()

axiosInstance.interceptors.request.use(
    (config) => {
        if (!axiosInstance.defaults.baseURL) {
            throw new Error('API URL needs to be configured')
        }

        if (mock.screenshotting()) {
            const method = (config.method ?? 'UNKNOWN').toString().toUpperCase()
            console.error(`Attempted to make request to ${method} ${config.url} while screenshotting`)
            throw new Error('Screenshotting is enabled, API requests should not be made')
        }

        const isApiRequest = startsWith(config.url, '/')
            || startsWith(config.url, axiosInstance.defaults.baseURL)

        // If the request isn't an API request don't add our authentication servers
        if (!isApiRequest) {
            console.log('Request is not an API request, skipping auth')
            return config
        }

        const appStore = useAppStore()
        const authStore = useAuthStore()

        // Add the bearer token
        if (!config.headers.Authorization && authStore.token) {
            config.headers.Authorization = `Bearer ${authStore.token}`
        }

        // Add the user decryption key
        if (!config.headers['X-USER-KEY'] && authStore.userKey) {
            config.headers['X-USER-KEY'] = authStore.userKey
        }

        // Add the mock data header
        if (appStore.mockData) {
            config.headers['X-MOCK-DATA'] = true
        }

        if (appStore.draftVersion) {
            config.headers['X-APP-VERSION'] = appStore.draftVersion
        }

        // Add the request id
        config.headers['X-BEYOND-REQUEST-ID'] = uuidv4().toString()
        config.headers['X-BEYOND-REQUESTED-AT'] = dayjs().toISOString()

        return config
    },
    (error) => {
        return Promise.reject(error)
    },
)

axiosInstance.interceptors.response.use(
    (response) => {
        const authStore = useAuthStore()

        // Check if we get a X-REFRESH-REQUIRED header and trigger a refresh
        // Axios converts headers to lowercase for consistency
        if (has(response.headers, 'x-refresh-required')) {
            const requestedAtHeader = response.config?.headers?.['X-BEYOND-REQUESTED-AT']
            const requestedAt = requestedAtHeader ? dayjs(requestedAtHeader) : null

            console.info('Token refresh required')
            authStore.refresh(requestedAt)
        }
        return response
    },
    (error) => {
        const authStore = useAuthStore()
        if (get(error, 'response.status') === 401) {
            authStore.logout()

            // Navigate back
            window.location.href = '/'
        }

        // Check if the error is maintenance mode
        if (get(error, 'response.status') === 503
            && get(error, 'response.data.type') === 'App\\Exceptions\\MaintenanceModeException') {
            window.localStorage.setItem('maintenance.title', get(error, 'response.data.title'))
            window.localStorage.setItem('maintenance.message', get(error, 'response.data.message'))
            window.location.href = '/maintenance'
        }

        // If is a validation error, we don't log out the user
        if (get(error, 'response.status') === 422) {
            throw error
        }

        // If error was not 401 just reject as is
        throw error
    },
)

export default axiosInstance
